import { TypedUseSelectorHook, useDispatch, useSelector } from 'react-redux';
import {
  combineReducers,
  configureStore,
  PreloadedState
} from '@reduxjs/toolkit';
import { CurriedGetDefaultMiddleware } from '@reduxjs/toolkit/dist/getDefaultMiddleware';

import { notificationSlice } from '@components/notification/NotificationSlice';
import { adminSlice } from '@features/admin/slices/adminSlice';
import { authSlice } from '@features/authentication/slices/AuthSlice';
import { dashboardSlice } from '@features/dashboard/slices/dashboardSlice';
import { downloadsSlice } from '@features/downloads/downloadsSlice';
import { eventsSlice } from '@features/event-registration/slices/EventsSlice';
import { groupSlice } from '@features/group-management/slices/groupSlice';
import { metaSyncSlice } from '@features/lead-management/metaSyncSlice';
import { leadActivitiesSlice } from '@features/lead-management/slices/leadActivitiesSlice';
import { leadSlice } from '@features/lead-management/slices/leadSlice';
import { memberSlice } from '@features/member-management/slices/MemberSlice';
import { branchSlice } from '@layouts/main-header/branchSlice';
import { userSlice } from '@layouts/main-header/userSlice';

import { api } from './baseApi';
import storeLogger from './logger';

const middlewareList = (getDefaultMiddleware: CurriedGetDefaultMiddleware) => {
  const list = [api.middleware];
  if (process.env.NODE_ENV === 'development') {
    list.push(storeLogger);
  }
  return getDefaultMiddleware().concat(list);
};

// TODO: Remove leadsAPI from here after BE is deployed
export const rootReducer = combineReducers({
  [api.reducerPath]: api.reducer,
  notification: notificationSlice.reducer,
  downloads: downloadsSlice.reducer,
  metaSync: metaSyncSlice.reducer,
  group: groupSlice.reducer,
  lead: leadSlice.reducer,
  leadActivities: leadActivitiesSlice.reducer,
  auth: authSlice.reducer,
  user: userSlice.reducer,
  admin: adminSlice.reducer,
  member: memberSlice.reducer,
  events: eventsSlice.reducer,
  branch: branchSlice.reducer,
  dashboard: dashboardSlice.reducer
});
export const setupStore = (preloadedState?: PreloadedState<RootState>) =>
  configureStore({
    reducer: rootReducer,
    middleware: (getDefaultMiddleware: CurriedGetDefaultMiddleware) =>
      middlewareList(getDefaultMiddleware),
    preloadedState
  });

export const store = setupStore({});

export type RootState = ReturnType<typeof rootReducer>;
export type AppDispatch = typeof store.dispatch;
export type AppStore = ReturnType<typeof setupStore>;
export const useAppDispatch: () => AppDispatch = useDispatch;
export const useAppSelector: TypedUseSelectorHook<RootState> = useSelector;
